import { request } from '@/shared/api/http'
import type {
  BackupCodesResponse, BalancesResponse, ChartResponse, FiatCurrency,
  MessageResponse, OverviewResponse, TwoFactorSetupResponse, UserResponse,
} from '@/shared/api/types'

/** Профиль, балансы, сводка и настройки аккаунта. */
export const account = {
  me() {
    return request<UserResponse>('/me')
  },
  // `currency_view` — в какой фиатной валюте считать. Переключатель
  // USD/RUB в шапке кабинета управляет именно им, а не пересчитывает
  // числа на фронте: пересчёт по своему курсу дал бы суммы, не сходящиеся
  // с теми, что в истории и в заявках.
  balances(view: FiatCurrency = 'usd') {
    return request<BalancesResponse>('/balances', { query: { currency_view: view } })
  },
  overview(view: FiatCurrency = 'usd') {
    return request<OverviewResponse>('/stats/overview', { query: { currency_view: view } })
  },
  incomeChart(days = 7, view: FiatCurrency = 'usd') {
    return request<ChartResponse>('/stats/income-chart', {
      query: { days, currency_view: view },
    })
  },
  /**
   * Заказать смену пароля. В ответ — способ подтверждения.
   *
   * Текущий пароль не спрашивается: по макету подтверждение — код,
   * который уходит на текущую почту или берётся из приложения.
   * `method` решает, какой текст показать в окне подтверждения:
   * ошибиться здесь значит отправить человека искать письмо,
   * которого нет, и наоборот.
   */
  requestPasswordChange(newPassword: string) {
    return request<{ method: 'totp' | 'email' }>('/settings/password/request', {
      method: 'POST',
      body: { new_password: newPassword },
    })
  },

  /**
   * Подтвердить смену пароля. После неё все токены погашены —
   * текущая сессия мертва, нужен новый вход.
   */
  confirmPasswordChange(code: string) {
    return request<MessageResponse>('/settings/password/confirm', {
      method: 'POST', body: { code },
    })
  },
  /** Часовой пояс аккаунта. Бекенд отдаёт обновлённый профиль целиком. */
  setTimezone(timezone: string) {
    return request<UserResponse>('/settings/timezone', {
      method: 'PUT', body: { timezone },
    })
  },

  /**
   * Заказать смену почты. Код уходит на ТЕКУЩИЙ адрес.
   *
   * Не на новый: код на новый адрес доказал бы только, что адрес
   * существует, — угнанная сессия вписала бы свой и подтвердила сама.
   *
   * Ответ одинаков и когда письмо ушло, и когда адрес уже занят:
   * бекенд намеренно не даёт отличить одно от другого, иначе форма
   * превращается в проверку чужих почт на существование.
   */
  requestEmailChange(newEmail: string) {
    return request<MessageResponse>('/settings/email/request', {
      method: 'POST',
      body: { new_email: newEmail },
    })
  },

  /** Подтвердить смену. После неё все токены погашены — нужен новый вход. */
  confirmEmailChange(code: string) {
    return request<MessageResponse>('/settings/email/confirm', {
      method: 'POST', body: { code },
    })
  },

  /** Начало подключения 2FA: секрет и ссылка для приложения. */
  setupTwoFactor() {
    return request<TwoFactorSetupResponse>('/settings/2fa/setup', { method: 'POST' })
  },
  /** Подтверждение кодом. В ответ — резервные коды, показать их ОДИН раз. */
  enableTwoFactor(code: string) {
    return request<BackupCodesResponse>('/settings/2fa/enable', {
      method: 'POST', body: { code },
    })
  },
  disableTwoFactor(code: string) {
    return request<MessageResponse>('/settings/2fa/disable', {
      method: 'POST', body: { code },
    })
  },
}
